import { css } from "@emotion/css";
import { Button, StepCard } from "@octopusdeploy/design-system-components";
import { PlusIcon } from "@octopusdeploy/design-system-icons";
import { Avatar } from "@octopusdeploy/design-system-octopus-components";
import { space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import type { PublishedAndSharedProjectTemplateSummary } from "@octopusdeploy/octopus-server-client";
import type { ReactNode } from "react";
import { useState } from "react";
import { repository } from "~/clientInstance";
import type { DoBusyTask } from "~/components/DataBaseComponent";
import { GuidedSetupFrame } from "~/components/OnboardingDialog/GuidedSetupFrame";
import addProjectImageDark from "~/components/ProjectBasedActivation/assets/img-addproject-dark.svg";
import addProjectImageLight from "~/components/ProjectBasedActivation/assets/img-addproject-light.svg";
import ProjectTemplateCardList from "./ProjectTemplateCardList";
interface NewProjectPageProps {
    title: ReactNode;
    projectTemplates: PublishedAndSharedProjectTemplateSummary[];
    isOnboarding?: boolean;
    onTemplateSkipped: () => void;
    onTemplateSelected?: (template: PublishedAndSharedProjectTemplateSummary) => void;
    close: () => void;
    spaceHasEnvironments: boolean;
    doBusyTask: DoBusyTask;
}
interface CreateBlankProjectCardProps {
    onClick: () => void;
}
function ProjectTemplateSelectionPage(props: NewProjectPageProps) {
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [selectedTemplateSummary, setSelectedTemplateSummary] = useState<PublishedAndSharedProjectTemplateSummary | null>(null);
    const handlePreviewClick = async (template: PublishedAndSharedProjectTemplateSummary) => {
        setSelectedTemplateSummary(template);
        setIsDrawerOpen(true);
    };
    const handleDrawerClose = () => {
        setIsDrawerOpen(false);
        setSelectedTemplateSummary(null);
    };
    const handleUseTemplateClick = (template: PublishedAndSharedProjectTemplateSummary) => {
        props.onTemplateSelected?.(template);
    };
    const helpPanelContent = (<div>
            <strong>Project Templates</strong> are used for standardization of projects and their dependencies. Get your project up and deployed quickly with a Project Template.
        </div>);
    function getTemplateName(template: PublishedAndSharedProjectTemplateSummary): string {
        return template.Name;
    }
    function getTemplateIcon(template: PublishedAndSharedProjectTemplateSummary) {
        return template.Icon ?? undefined;
    }
    function getTemplateDescription(template: PublishedAndSharedProjectTemplateSummary) {
        return template.Description ?? undefined;
    }
    function getAvailableVersions(template: PublishedAndSharedProjectTemplateSummary): string[] {
        return [template.LatestVersion?.Version, template.LatestPreReleaseVersion?.Version].filter((v): v is string => v !== null && v !== undefined);
    }
    function getPrimaryAction(template: PublishedAndSharedProjectTemplateSummary) {
        return {
            label: "Use Template",
            onClick: () => handleUseTemplateClick(template),
        };
    }
    return (<GuidedSetupFrame helpPanelContent={helpPanelContent} helpPanelImage={{ src: addProjectImageLight, altText: "Add New Project", darkThemeSrc: addProjectImageDark }} showThemeSelector={props.isOnboarding} onClose={props.close} isLeftPanelWide={true}>
            <>
                <div className={styles.title}>{props.title}</div>
                <div className={styles.subheading}>Add Project</div>
                <div className={styles.heading}>Template Selection</div>

                {props.projectTemplates.length > 0 && (<ProjectTemplateCardList>
                        {[
                <CreateBlankProjectCard key={"create-blank-project-card"} onClick={props.onTemplateSkipped}/>,
                ...props.projectTemplates.map((projectTemplate) => {
                    const templateName = getTemplateName(projectTemplate);
                    const templateIcon = getTemplateIcon(projectTemplate);
                    const templateDescription = getTemplateDescription(projectTemplate);
                    const availableVersions = getAvailableVersions(projectTemplate);
                    return (<StepCard key={`project-template-${projectTemplate.Slug}-card`} heading={templateName} subHeading={availableVersions.join(", ")} logo={<Avatar type="template" size="large" src={repository.Icons.getIconUrl(templateIcon)} alt={`Icon for the project template called ${templateName}`}/>} description={templateDescription ? <div className={styles.truncatedDescription}>{templateDescription}</div> : undefined} primaryAction={getPrimaryAction(projectTemplate)} secondaryAction={{
                            label: "Preview",
                            onClick: () => {
                                handlePreviewClick(projectTemplate);
                            },
                        }}/>);
                }),
            ]}
                    </ProjectTemplateCardList>)}
            </>
            {/* TODO: we need a non-blocking drawer component here */}
            {/* {isProjectTemplatesFeatureToggled && isDrawerOpen && selectedTemplateSummary && (
            <Drawer
                isOpen={isDrawerOpen}
                onClose={handleDrawerClose}
                title=""
                subtitle=""
                variant="customActions"
                layout="basic"
                width="narrow"
            >
                <ProjectTemplatePanel
                    projectTemplate={selectedTemplateSummary}
                    onClick={handleUseTemplate}
                />
            </Drawer>
        )} */}
        </GuidedSetupFrame>);
}
export default ProjectTemplateSelectionPage;
function CreateBlankProjectCard({ onClick }: CreateBlankProjectCardProps) {
    return (<div className={styles.createBlankProjectCard.base}>
            <Button importance="quiet" size="medium" label={"Create Blank Project"} icon={<PlusIcon size={20}/>} onClick={onClick}/>
        </div>);
}
const styles = {
    title: css({
        marginTop: space[40],
        marginBottom: space[16],
    }),
    subheading: css({
        marginTop: space[32],
        font: text.body.regular.medium,
        color: themeTokens.color.text.secondary,
    }),
    heading: css({
        marginBottom: space[32],
        font: text.heading.large,
    }),
    truncatedDescription: css({
        display: "-webkit-box",
        WebkitLineClamp: 2,
        textOverflow: "ellipsis",
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
    }),
    createBlankProjectCard: {
        base: css({
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: themeTokens.color.background.info,
        }),
    },
};
